import React, { useEffect } from "react";
import { Doughnut } from "react-chartjs-2";
import { Card, Typography } from "antd";
import { TooltipItem } from "chart.js";
import usePatientsStatusData, { StatusData } from "../../hooks/patients/usePatientsStatusData";
import BillableChartSkeleton from "./BillableChartSkeleton";
import styles from "./RTMDashboard.module.css";
import css from "./BillableChart.module.css";

interface BillableChartProps {
  className?: string;
  style?: React.CSSProperties;
}

const { Text, Title } = Typography;

const BillableChart: React.FC<BillableChartProps> = ({ className, style }) => {
  const { patientsChartData, statusData, getPatientsStatusData, patientsStatusLoading } = usePatientsStatusData();

  useEffect(() => {
    getPatientsStatusData();
  }, []);

  const defaultData = {
    labels: ["Active", "Inactive", "Pending"],
    datasets: [{
      data: [0, 0, 100],
      backgroundColor: ["#1D4E87", "#0170AE", "#4A90E2"],
      borderWidth: 0,
    }]
  };

  const chartData = patientsChartData || defaultData;

  const chartOptions = {
    cutout: "50%",
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context: TooltipItem<"doughnut">) {
            return `${context.label}: ${context.parsed}`;
          },
        },
      },
    },
    maintainAspectRatio: false,
  };

  if (patientsStatusLoading) {
    return <BillableChartSkeleton />;
  }

  return (
    <Card
      title="Patients by Status"
      className={`${styles.chartCard} ${css.card} ${className}`}
      style={style}
      styles={{
        header: { borderBottom: '0.0625rem solid #f0f0f0' },
        body: { display: 'flex', flexDirection: 'column', height: 'calc(100% - 3.5625rem)' }
      }}
    >
      <div className={css.chartContainer}>
        <Doughnut
          data={chartData}
          options={{
            ...chartOptions,
            responsive: true,
            maintainAspectRatio: false,
          }}
        />
      </div>
      <div className={styles.legendContainer}>
        <div className={css.legendItem}>
          <span className={`${styles.legendColor} ${css.legendColor} ${css.activeColor}`}></span>
          <Text>
            Active: {statusData?.active.count || 0} ({statusData?.active.percentage || 0}%)
          </Text>
        </div>
        <div className={css.legendItem}>
          <span className={`${styles.legendColor} ${css.legendColor} ${css.inactiveColor}`}></span>
          <Text>
            Inactive: {statusData?.inactive.count || 0} ({statusData?.inactive.percentage || 0}%)
          </Text>
        </div>
        <div className={css.legendItem}>
          <span className={`${styles.legendColor} ${css.legendColor} ${css.initializedColor}`}></span>
          <Text>
            Pending: {statusData?.pending.count || 0} ({statusData?.pending.percentage || 0}%)
          </Text>
        </div>
      </div>
    </Card>
  );
};

export default BillableChart;